/**
 * Two-layer health.
 *
 * A fight is not lost by running a single bar to zero. A fighter is stopped
 * when they go out — that is *consciousness* — but how close to going out they
 * are depends on how much punishment the body has already absorbed, which is
 * *wellness*. So:
 *
 *   wellness       physical condition. Drops slowly, never recovers during a
 *                  fight, and acts as the ceiling on consciousness.
 *   consciousness  how present the fighter is right now. Drops fast, and
 *                  recovers between exchanges — but only up to the ceiling.
 *
 * The consequence is the interesting part: early on, a fighter shrugs off a
 * clean hit in a second or two. Late on, the ceiling has fallen far enough that
 * the same hit leaves them on the edge with nowhere to recover to.
 *
 * Pure module — no rendering, no three.js, no timers of its own. Feed it `dt`.
 */

export const DEFAULT_HEALTH = Object.freeze({
  maxConsciousness: 100,
  maxWellness: 100,
  /** A hit costs this much more consciousness than wellness. */
  consciousnessMultiplier: 2.4,
  /** Consciousness regained per second, once regen starts. */
  regenPerSecond: 9,
  /** Seconds after the last hit before regen starts. */
  regenDelay: 1.1,
  /** Fraction of max consciousness below which the fighter is in danger. */
  dangerThreshold: 0.25,
  /** Fraction it must climb back past to leave danger. Hysteresis, so the flag
   *  does not flicker while regen hovers on the line. */
  dangerRecovery: 0.34,
  /**
   * Consciousness a blow that is not allowed to finish the fight must leave
   * behind. Callers opt in by passing `floor`; the model itself has no opinion
   * about which blows those are.
   */
  survivalFloor: 1,
  /**
   * A third pool, one per limb, for the styles that work a joint rather than a
   * jaw. It drains only when a blow names the limb it landed on, it never
   * recovers, and on its own it decides nothing — a limb at zero is a fact the
   * fight layer may or may not care about, exactly like `isOut`.
   */
  limbHealth: 45,
  limbs: Object.freeze(['rightArm', 'leftArm', 'rightLeg', 'leftLeg']),
});

export function createHealth(options = {}) {
  const config = Object.freeze({ ...DEFAULT_HEALTH, ...options });

  let consciousness = config.maxConsciousness;
  let wellness = config.maxWellness;
  let timeSinceDamage = Infinity;
  let inDanger = false;
  /** One pool per limb. Drains only when a blow names one, never recovers. */
  let limbs = new Map(config.limbs.map((name) => [name, config.limbHealth]));

  /** Consciousness can never exceed the body's current condition. */
  function ceiling() {
    return Math.min(wellness, config.maxConsciousness);
  }

  function updateDanger() {
    const threshold = config.dangerThreshold * config.maxConsciousness;
    const recovery = config.dangerRecovery * config.maxConsciousness;
    if (inDanger) {
      if (consciousness > recovery) inDanger = false;
    } else if (consciousness < threshold) {
      inDanger = true;
    }
  }

  const api = {
    get consciousness() {
      return consciousness;
    },
    get wellness() {
      return wellness;
    },
    /** The current cap on consciousness — the wellness bar, in practice. */
    get ceiling() {
      return ceiling();
    },
    get inDanger() {
      return inDanger;
    },
    /** Out cold. Nothing here decides what that means for the fight. */
    get isOut() {
      return consciousness <= 0;
    },
    /** The body has given out, regardless of whether the fighter is awake. */
    get isSpent() {
      return wellness <= 0;
    },

    /** What is left of one limb, or null if that part is not one. */
    limb: (name) => (limbs.has(name) ? limbs.get(name) : null),
    /** The limbs this body has, in the order they were declared. */
    get limbNames() {
      return [...limbs.keys()];
    },
    /** Every limb as a fraction of whole, for drawing. */
    limbFractions() {
      return Object.fromEntries(
        [...limbs].map(([name, left]) => [name, left / config.limbHealth])
      );
    },
    /** The first limb that has gone, or null. */
    get brokenLimb() {
      for (const [name, left] of limbs) if (left <= 0) return name;
      return null;
    },
    /**
     * Whether a blow of this size on this part would finish that limb.
     *
     * Asked *before* the damage lands, because the fight layer has to decide
     * whether a blow is allowed to finish somebody before it applies it —
     * otherwise the survival floor and the knockout disagree.
     */
    wouldBreakLimb(part, amount, limbScale = 1) {
      if (!limbs.has(part) || !(amount > 0)) return false;
      return limbs.get(part) - amount * limbScale <= 0;
    },
    get timeSinceDamage() {
      return timeSinceDamage;
    },
    get regenerating() {
      return timeSinceDamage >= config.regenDelay && consciousness < ceiling();
    },
    config,

    /** Both bars as fractions of their maxima, for drawing. */
    fractions() {
      return {
        consciousness: consciousness / config.maxConsciousness,
        wellness: wellness / config.maxWellness,
        ceiling: ceiling() / config.maxConsciousness,
      };
    },

    snapshot() {
      return Object.freeze({
        consciousness,
        wellness,
        ceiling: ceiling(),
        inDanger,
        isOut: api.isOut,
        isSpent: api.isSpent,
        regenerating: api.regenerating,
      });
    },

    /**
     * Applies a hit. `amount` is wellness damage; consciousness takes a
     * multiple of it, which is what makes a fighter wobble long before their
     * body is finished. Pass `consciousnessMultiplier` per hit to distinguish a
     * clean head shot from a body shot.
     *
     * Returns what each bar actually lost, after clamping.
     */
    applyDamage(
      amount,
      {
        consciousnessMultiplier = config.consciousnessMultiplier,
        floor = 0,
        /** How much of the wellness damage actually sticks. A style's ratio. */
        wellnessScale = 1,
        /** Which limb took it, if any, and how hard the move works one. */
        part = null,
        limbScale = 1,
      } = {}
    ) {
      if (!(amount > 0)) {
        return { wellness: 0, consciousness: 0, floored: false, limb: null, limbLost: 0 };
      }

      const wellnessBefore = wellness;
      const consciousnessBefore = consciousness;

      // A blow that names a limb works that limb as well as the body. It is a
      // third pool, not a share of the first two: an armbar takes the arm apart
      // without taking anything else off.
      let limb = null;
      let limbLost = 0;
      if (limbs.has(part)) {
        const before = limbs.get(part);
        const left = Math.max(0, before - amount * limbScale);
        limbs.set(part, left);
        limb = part;
        limbLost = before - left;
      }

      wellness = Math.max(0, wellness - amount * wellnessScale);
      consciousness = Math.max(0, consciousness - amount * consciousnessMultiplier);
      // A body that just took damage cannot be holding up more awareness
      // than it has left.
      consciousness = Math.min(consciousness, ceiling());

      // The floor goes on last, after the ceiling, so a fighter whose body has
      // given out entirely is still left standing by a blow that is not allowed
      // to finish them. It is the only place consciousness may sit above its
      // own ceiling, and it means exactly what it looks like: they are upright
      // on nothing but the fact that this punch was not the one.
      const floored = floor > 0 && consciousness < floor;
      if (floored) consciousness = floor;

      timeSinceDamage = 0;
      updateDanger();

      return {
        wellness: wellnessBefore - wellness,
        consciousness: consciousnessBefore - consciousness,
        floored,
        limb,
        limbLost,
      };
    },

    /** Drains consciousness only — chokes, exhaustion, gas. Leaves the body. */
    drainConsciousness(amount, { floor = 0 } = {}) {
      if (!(amount > 0)) return 0;
      const before = consciousness;
      consciousness = Math.max(floor, consciousness - amount);
      timeSinceDamage = 0;
      updateDanger();
      return before - consciousness;
    },

    update(dt) {
      if (!(dt > 0)) return;
      timeSinceDamage += dt;
      if (timeSinceDamage >= config.regenDelay) {
        consciousness = Math.min(ceiling(), consciousness + config.regenPerSecond * dt);
      }
      updateDanger();
    },

    reset() {
      consciousness = config.maxConsciousness;
      wellness = config.maxWellness;
      timeSinceDamage = Infinity;
      inDanger = false;
      limbs = new Map(config.limbs.map((name) => [name, config.limbHealth]));
    },
  };

  return api;
}
